import { useState, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import { authedGet, authedPut } from '@/lib/api';

/** One carbon row for the unified writer (keys are supplied separately). */
export interface TransactionCarbonRow {
  account_id: string;
  emission_table_id: string;
  dimension_values: Record<string, string>;
  quantity: number;
  quantity_unit: string | null;
  quantity_unit_code: string | null;
  /** Quantity pre-converted to the factor's input unit (the multiplier the RPC uses). */
  quantity_normalized: number | null;
}

/**
 * Persist a transaction/expense/AP/AR's carbon rows through the backend
 * `PUT /api/v1/emissions/orgs/:orgId/carbon-results` route, which wraps the
 * unified `save_transaction_carbon_results` RPC (migration 20260709101342).
 * The RPC deletes existing rows for the key(s) then bulk-inserts, computing
 * each row's emission server-side — one source of truth, and delete-by-key
 * fixes the historic dup-on-edit bug. Pass the FULL current set of carbon rows
 * (an empty array clears the transaction's carbon). Direct client writes are
 * blocked by the RESTRICTIVE verify_backend_token() policy (migration
 * 20260713121731).
 */
export async function persistTransactionCarbonResults(
  organizationId: string,
  keys: { transactionId?: string | null; apInvoiceId?: string | null; arInvoiceId?: string | null },
  rows: TransactionCarbonRow[],
): Promise<void> {
  if (!keys.transactionId && !keys.apInvoiceId && !keys.arInvoiceId) {
    throw new Error('persistTransactionCarbonResults requires a transactionId, apInvoiceId or arInvoiceId');
  }
  await authedPut<{ count: number }>(
    `/api/v1/emissions/orgs/${organizationId}/carbon-results`,
    {
      transaction_id: keys.transactionId ?? null,
      ap_invoice_id: keys.apInvoiceId ?? null,
      // Sent only when present — the AR key is the newest one on the route.
      ...(keys.arInvoiceId ? { ar_invoice_id: keys.arInvoiceId } : {}),
      rows,
    },
  );
}

/**
 * A stored carbon row as returned by the backend hydration GET — the subset of
 * carbon_emission_results columns the forms consume.
 */
export interface StoredCarbonResultRow {
  account_id: string | null;
  emission_table_id: string | null;
  dimension_values: Record<string, string> | null;
  quantity: number | null;
  quantity_unit_code: string | null;
  multiplier_value: number | null;
  emission_factor_id: string | null;
  emission_factor_value: number | null;
  calculated_emissions: number | null;
}

/** Map a per-line CarbonData + its account into a unified-writer row. */
export function carbonDataToRow(accountId: string, cd: CarbonData): TransactionCarbonRow {
  return {
    account_id: accountId,
    emission_table_id: cd.table_id,
    dimension_values: cd.dimension_values,
    quantity: cd.quantity,
    quantity_unit: cd.quantity_unit_code,
    quantity_unit_code: cd.quantity_unit_code,
    quantity_normalized: cd.multiplier_value,
  };
}

export interface CarbonData {
  table_id: string;
  dimension_values: Record<string, string>;
  /** Raw user-entered amount, in `quantity_unit_code`. Preserved for
   *  round-tripping the input field and matches the activity path's
   *  `quantity` column. */
  quantity: number;
  /** Unit code of `quantity` (e.g. 'kg'). Mirrors the account link's
   *  `multiplier_unit_code`; null when no unit is configured. */
  quantity_unit_code: string | null;
  /** The multiplier that actually went into the emission math, in the
   *  factor's native input unit. Equals `quantity` when no conversion
   *  was needed. Persisted so
   *  `multiplier_value × emission_factor_value = calculated_emissions`. */
  multiplier_value: number;
  emission_factor_id?: string;
  emission_factor_value?: number;
  calculated_emissions?: number;
}

interface UseCarbonTrackingOptions {
  organizationId: string;
  transactionId?: string;  // For expenses/invoices that have a related transaction
  /**
   * For AP bills, which only get a ledger transaction on approval. Carbon data
   * entered pre-approval is keyed by this invoice id; approve_ap_invoice later
   * backfills transaction_id onto those rows. Rows may carry one or both keys.
   */
  apInvoiceId?: string;
  /**
   * For AR invoices (post-on-issue, 20261101120000): a draft books nothing,
   * so its carbon keys on the invoice and `issue_ar_invoice` graduates it onto
   * the posting (stamping `transaction_id`). Rows may carry one or both keys.
   */
  arInvoiceId?: string;
  /**
   * Reactive account-ids for each line item in render order. The caller
   * should watch the form (e.g. `useWatch` or `form.watch`) and pass the
   * current list so stored carbon results can re-map when line items load
   * or the operator changes an account. Index-aligned with the form's
   * `line_items` array; use `undefined` for rows that don't yet have an
   * account selected.
   */
  lineItemAccountIds: Array<string | undefined>;
}

/**
 * The one thing `saveCarbonEmissions` reads off a form line: its account. An
 * expense / journal line names it `account_id`, an AP line `expense_account_id`
 * and an AR line `revenue_account_id` — the first one present wins.
 */
export interface CarbonLineItemAccount {
  account_id?: string | null;
  expense_account_id?: string | null;
  revenue_account_id?: string | null;
  /** The rest of the form line, which the save never reads. */
  [field: string]: unknown;
}

/**
 * Reusable hook for managing carbon tracking data across line items.
 * Used by expenses, AP invoices, AR invoices, and manual transactions.
 * Follows DRY principles by centralizing carbon data management logic.
 */
export function useCarbonTracking({
  organizationId,
  transactionId,
  apInvoiceId,
  arInvoiceId,
  lineItemAccountIds,
}: UseCarbonTrackingOptions) {
  const [manualCarbonData, setManualCarbonData] = useState<Record<number, CarbonData | null>>({});
  const queryClient = useQueryClient();

  // Fetch existing carbon results when editing. Match by whichever key(s)
  // exist: pre-approval AP-bill carbon is keyed by ap_invoice_id only; once
  // approved it also carries transaction_id (graduated by approve_ap_invoice),
  // so a graduated row matches either. The ['carbonEmissions', orgId] key
  // prefix means existing carbon invalidations (activities, expense/AP/AR
  // mutations) refresh this hydration query automatically.
  // Errors are NOT caught here: the global QueryCache.onError (App.tsx)
  // reports every failed query to Sentry with its queryKey, and the
  // hydration-readiness guard in saveCarbonEmissions keeps a failed fetch
  // from being mistaken for "this entry has no carbon".
  const { data: storedResults, isLoading, isSuccess } = useQuery({
    queryKey: ['carbonEmissions', organizationId, 'entry', transactionId ?? null, apInvoiceId ?? null, arInvoiceId ?? null],
    enabled: Boolean(organizationId && (transactionId || apInvoiceId || arInvoiceId)),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (transactionId) params.set('transactionId', transactionId);
      if (apInvoiceId) params.set('apInvoiceId', apInvoiceId);
      if (arInvoiceId) params.set('arInvoiceId', arInvoiceId);
      const { results } = await authedGet<{ results: StoredCarbonResultRow[] }>(
        `/api/v1/emissions/orgs/${organizationId}/carbon-results?${params}`,
      );
      return results;
    },
  });

  // Hydration readiness, read at save time. In edit mode (an entry key is
  // present) the stored rows are part of the full-replace payload — saving
  // before they have loaded (or after the fetch failed) would silently wipe
  // the entry's stored carbon, so saveCarbonEmissions refuses until the
  // query has succeeded at least once. Create mode has nothing to hydrate.
  const hydrationReadyRef = useRef(true);
  hydrationReadyRef.current = !(transactionId || apInvoiceId || arInvoiceId) || isSuccess;

  // Store carbon results by account_id for easy lookup
  const carbonResultsByAccountId = useMemo(() => {
    const resultsByAccountId: Record<string, StoredCarbonResultRow> = {};
    (storedResults ?? []).forEach(result => {
      if (result.account_id) {
        resultsByAccountId[result.account_id] = result;
      }
    });
    return resultsByAccountId;
  }, [storedResults]);

  // Serialize the reactive account-id list so the memo depends on a stable
  // primitive key rather than an array reference (which would thrash on
  // every form render). JSON is fine here: the list is small (handful of
  // line items) and only contains uuids/undefined.
  const lineItemAccountIdsKey = useMemo(
    () => JSON.stringify(lineItemAccountIds),
    [lineItemAccountIds],
  );

  // Map stored carbon results to the form's current line items. Re-runs
  // when either the stored results arrive OR the form's line items
  // populate/change — this is the edit-mode race fix: on reopen, the
  // expense's line items are written into the form by a downstream
  // `form.reset()`, which may land after the carbon fetch completes.
  const carbonDataByLineItem = useMemo(() => {
    const result: Record<number, CarbonData | null> = {};

    if (Object.keys(carbonResultsByAccountId).length > 0) {
      lineItemAccountIds.forEach((accountId, index) => {
        if (!accountId) return;
        const matchingResult = carbonResultsByAccountId[accountId];
        if (!matchingResult) return;

        // `quantity` preserves what the user typed (e.g. 12 kg) so the
        // NumberInput can round-trip it. Pre-fix rows have NULL
        // `quantity`; fall back to `multiplier_value` which stored the
        // raw input for them.
        const rawQuantity = matchingResult.quantity ?? matchingResult.multiplier_value;
        // Transaction rows always carry a table id + quantity (the RPC RAISEs
        // without them); the casts preserve legacy-row pass-through untyped
        // rows had before the read moved behind the API.
        result[index] = {
          table_id: matchingResult.emission_table_id as string,
          dimension_values: matchingResult.dimension_values as Record<string, string>,
          quantity: rawQuantity as number,
          quantity_unit_code: matchingResult.quantity_unit_code ?? null,
          multiplier_value: matchingResult.multiplier_value as number,
          emission_factor_id: matchingResult.emission_factor_id ?? undefined,
          emission_factor_value: matchingResult.emission_factor_value ?? undefined,
          calculated_emissions: matchingResult.calculated_emissions,
        };
      });
    }

    return result;
    // lineItemAccountIds is tracked via its serialized key to avoid array-
    // reference churn. The concrete array read inside the callback is still
    // fresh because every render re-captures it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carbonResultsByAccountId, lineItemAccountIdsKey]);

  // Merge with manual updates (for new/edited items)
  const finalCarbonData = useMemo(
    () => ({ ...carbonDataByLineItem, ...manualCarbonData }),
    [carbonDataByLineItem, manualCarbonData]
  );

  // Latest merged data, read at save time without making saveCarbonEmissions
  // change identity every render.
  const finalCarbonDataRef = useRef(finalCarbonData);
  finalCarbonDataRef.current = finalCarbonData;

  // Update carbon data for a specific line item
  const updateCarbonData = useCallback((index: number, data: CarbonData | null) => {
    setManualCarbonData(prev => ({
      ...prev,
      [index]: data
    }));
  }, []);

  // Clear all carbon data (e.g., when form is reset or right after a save).
  // Manual edits are dropped AND the cached hydration rows are removed — a
  // save that deleted a line's carbon must not fall back to the stale cached
  // row (which would re-display, and on a second save re-persist, the carbon
  // the user just removed). The save's own invalidation refetches fresh rows.
  const clearCarbonData = useCallback(() => {
    setManualCarbonData({});
    queryClient.removeQueries({
      queryKey: ['carbonEmissions', organizationId, 'entry'],
    });
  }, [organizationId, queryClient]);

  // Save carbon emissions via the unified RPC. Rows are keyed by transaction_id
  // and/or ap_invoice_id — pass whichever the caller has (AP bills only have an
  // invoice id until they're approved). The RPC deletes existing rows for the
  // key(s) and re-inserts, computing each emission server-side, so we send the
  // FULL current set (merged stored + session edits) and no separate delete is
  // needed. An empty set clears the transaction's carbon.
  const saveCarbonEmissions = useCallback(async (
    keys: { transactionId?: string; apInvoiceId?: string; arInvoiceId?: string },
    lineItems: ReadonlyArray<CarbonLineItemAccount | undefined>
  ) => {
    if (!hydrationReadyRef.current) {
      // The stored rows haven't loaded (slow network, failed fetch, org
      // context still resolving) — an empty payload would delete them.
      throw new Error(
        'Stored carbon data has not finished loading; carbon was not saved to avoid overwriting it.',
      );
    }
    const rows = Object.entries(finalCarbonDataRef.current)
      .filter(([, carbonData]) => carbonData !== null)
      .map(([index, carbonData]) => {
        const lineItem = lineItems[parseInt(index)];
        const accountId =
          lineItem?.account_id || lineItem?.expense_account_id || lineItem?.revenue_account_id;
        if (!accountId) {
          console.error('No account ID found for carbon line item:', lineItem);
          return null;
        }
        return carbonDataToRow(accountId, carbonData!);
      })
      .filter((row): row is TransactionCarbonRow => row !== null);

    await persistTransactionCarbonResults(organizationId, keys, rows);
    // Refresh the hydration query and every carbon aggregate (dashboards,
    // summaries) — same convention as the activity mutations.
    invalidateCarbonCaches(queryClient, organizationId);
    // Carbon lands AFTER the expense mutation's own invalidation (the form
    // saves emissions post-create/update), so the expense surfaces
    // (Impact column / expense index carbon sums) must refresh again here.
    queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
    // (invalidateCarbonCaches above also refreshes the Impact slim index,
    // which lists these carbon rows — useSustainabilityActivityIndex contract.)
  }, [organizationId, queryClient]);

  return {
    carbonDataByLineItem: finalCarbonData,
    isLoading,
    updateCarbonData,
    clearCarbonData,
    saveCarbonEmissions,
    /**
     * True once the operator has entered/edited carbon data this session.
     * Derived from `manualCarbonData` (empty on load — fetched results live
     * separately in `carbonResultsByAccountId`), so it isolates real user
     * edits and does not false-positive when editing a carbon-tracked
     * transaction. Consumers use it to report form dirtiness.
     */
    hasManualCarbonEdits: Object.keys(manualCarbonData).length > 0,
  };
}
